/**
 * lib/status.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The one place a stored value becomes a colour and a label.
 *
 * The API stores stable machine values and the UI shows different words —
 * `imprest` displays as "limit", `allocation` displays as "assignment" (see
 * the spec delta §1 terminology change). Keeping the stored value stable and
 * the display mapping in ONE module means a future rename is a change here,
 * not a search across twelve screens. No component may hardcode a status
 * colour or re-spell a stored value.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { status as tones, type StatusTone } from '../theme/colors';

export type ToneName = keyof typeof tones;

/** Resolve a tone name to its four colours. */
export const toneOf = (name: ToneName): StatusTone => tones[name];

/* ── Reconciliation status ──────────────────────────────────────────────── */
const RECON: Record<string, { label: string; tone: ToneName }> = {
  matched: { label: 'matched', tone: 'positive' },
  short: { label: 'short', tone: 'danger' },
  over: { label: 'over', tone: 'warning' },
  waived: { label: 'waived', tone: 'neutral' },
};

/* ── Assignment status ──────────────────────────────────────────────────── */
const ASSIGNMENT: Record<string, { label: string; tone: ToneName }> = {
  // Active is intentionally NOT the green family: in the register every healthy
  // row would be green, which drowns the rows that actually need attention.
  // The design gives it the grey border / white fill instead.
  active: { label: 'active', tone: 'subtle' },
  suspended: { label: 'suspended', tone: 'warning' },
  closed: { label: 'closed', tone: 'neutral' },
};

/* ── Approval state ─────────────────────────────────────────────────────── */
const APPROVAL: Record<string, { label: string; tone: ToneName }> = {
  pending: { label: 'pending', tone: 'pending' },
  approved: { label: 'approved', tone: 'positive' },
  rejected: { label: 'rejected', tone: 'danger' },
};

/* ── Outbox / sync delivery ─────────────────────────────────────────────── */
const SYNC: Record<string, { label: string; tone: ToneName }> = {
  DELIVERED: { label: 'delivered', tone: 'positive' },
  PENDING: { label: 'pending', tone: 'pending' },
  FAILED: { label: 'failed', tone: 'danger' },
};

/* ── Separation (settlement alert) state ────────────────────────────────── */
const SEPARATION: Record<string, { label: string; tone: ToneName }> = {
  open: { label: 'settle before leaving', tone: 'warning' },
  settled: { label: 'settled', tone: 'positive' },
};

const FALLBACK = { label: '—', tone: 'neutral' as ToneName };

/** Look a value up in one of the maps above, tolerating an unknown value. */
function lookup(
  map: Record<string, { label: string; tone: ToneName }>,
  value: string | null | undefined,
): { label: string; tone: ToneName } {
  if (!value) return FALLBACK;
  return map[value] ?? map[value.toUpperCase()] ?? { label: value.replace(/_/g, ' '), tone: 'neutral' };
}

export const reconStatus = (v?: string | null) => lookup(RECON, v);
export const assignmentStatus = (v?: string | null) => lookup(ASSIGNMENT, v);
export const approvalState = (v?: string | null) => lookup(APPROVAL, v);
export const syncStatus = (v?: string | null) => lookup(SYNC, v);
export const separationState = (v?: string | null) => lookup(SEPARATION, v);

/* ── Reconciliation age ─────────────────────────────────────────────────── */
/**
 * The register's staleness chip. Thresholds are from the design:
 * ≤7d green, ≤20d amber, ≤60d orange, >60d red, and "never" is red — because
 * money nobody has ever counted is the worst case, not a neutral one.
 *
 * Note this is a DISPLAY ramp and is separate from `rules.staleDays`, which is
 * the org's own configurable definition used by the Overview KPI.
 */
export function staleness(days: number | null): ToneName {
  if (days === null) return 'danger';
  if (days <= 7) return 'positive';
  if (days <= 20) return 'pending';
  if (days <= 60) return 'warning';
  return 'danger';
}

/* ── Terminology: stored value → displayed word ─────────────────────────── */

/** Ledger transaction types. `allocation` is shown as "assignment". */
const TXN_LABEL: Record<string, string> = {
  allocation: 'assignment',
  spend: 'spend',
  collection: 'collection',
  deposit: 'deposit',
  replenishment: 'replenishment',
  reversal: 'reversal',
  return: 'return',
  adjustment: 'adjustment',
};

export const txnLabel = (v?: string | null): string =>
  (v && TXN_LABEL[v]) ?? (v ? v.replace(/_/g, ' ') : '—');

/**
 * Which way a transaction moves the custodian's balance. This drives the sign
 * on every ledger amount and the ink/grey colour split on the custodian's
 * activity list — inflows ink, outflows grey.
 *
 * A REVERSAL MIRRORS ITS ORIGINAL'S TYPE AND INVERTS ITS EFFECT. The reversal of
 * a spend is itself stored as a `spend` (the H1 ruling — a correction is a new
 * row, never a different kind of thing), with a positive amount and
 * `is_reversal: true`; the database's cash_txn_direction() flips the sign from
 * that flag, and domain/direction.ts is its TypeScript twin.
 *
 * This read the type alone, so a reversal rendered with the sign of the row it
 * was undoing: reversing a 620 spend put "− AED 620.00" on the ledger while the
 * balance went UP by 620. On the custodian's own screens that showed a refund
 * as a second debit against them.
 */
// Mirrors DIRECTION in the API's domain/direction.ts, which is itself the twin
// of cash_txn_direction() in SQL. `adjustment` was missing here, so a correcting
// adjustment — which RAISES the balance — was drawn as an outflow.
const INFLOWS = new Set(['allocation', 'collection', 'replenishment', 'adjustment']);
export const txnDirection = (v?: string | null, isReversal = false): 'in' | 'out' => {
  const inflow = !!v && INFLOWS.has(v);
  return inflow !== isReversal ? 'in' : 'out';
};

/** Reconciliation kind: stored `imprest`, displayed "limit". */
export const reconKindLabel = (v?: string | null): string =>
  v === 'imprest' ? 'limit' : v === 'collection' ? 'collection' : (v ?? '—');

/** Assignment scope. A fixed enum — never a configurable option list. */
export const SCOPES = ['open', 'purpose', 'collection'] as const;
export type Scope = (typeof SCOPES)[number];

const SCOPE_LABEL: Record<Scope, string> = {
  open: 'open',
  purpose: 'purpose',
  collection: 'collection',
};
export const scopeLabel = (v?: string | null): string =>
  (v && SCOPE_LABEL[v as Scope]) ?? (v ?? '—');

/** Chart series colour per scope — the only colour the charts use. */
export const scopeSeries = { open: 'open', purpose: 'purpose', collection: 'collection' } as const;

/** Purpose end rule, spelled out the way the drawer shows it. */
export const endRuleLabel = (v?: string | null): string =>
  v === 'return_to_company'
    ? 'unspent cash returns to the company'
    : v === 'release_to_holder'
      ? 'unspent cash is released to the holder'
      : '—';
